import { z } from 'zod';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';
import { employmentEpisodeIdSchema, withEpisode, SUCCESSOR_EPISODE_PLACEHOLDER } from '../episodeAddress';
import { DEFAULT_SOURCE_SYSTEM, toSourceContractId } from '../../../../constants/employmentSourceSystems';

/**
 * Validation schema for rehire
 */
const rehireSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6 — for REHIRE: the contract superseded, if any
  position: z.uuid(),
  // ADR-139 — nullable, and the null is load-bearing: it says the position this
  // rehire starts on requires no grade, so the handler clears the singleton instead
  // of inheriting the previous contract's. Omitted keeps the pre-ADR-139 meaning
  // ("unchanged"), which is what leaves the ingestion callers untouched.
  grade: z.uuid().nullable().optional(),
  contractType: z.uuid(),
  contractCompany: z.uuid(),
  workingLocation: z.uuid(),
  cluster: z.uuid(),
  hireDate: z.iso.date('Hire date must be in ISO format (YYYY-MM-DD)'),
  // ADR-136: optional contract number for the contract this rehire starts.
  // Absent -> the emitted batch is byte-for-byte today's.
  contractNumber: z.string().optional(),
  // ADR-138: which date to write onto the EMPLOYEE RECORD's hire date, chosen by the
  // operator from this person's employment episodes. Note this is NOT `hireDate`
  // above — that one is the new contract's start date, which dates the episode and
  // the HIRE_FINALIZED event and is required. Absent here -> the singleton is left
  // untouched, which is the pre-ADR-138 behaviour ("keep the current hire date").
  employeeHireDate: z.iso.date('Employee hire date must be in ISO format (YYYY-MM-DD)').optional(),
  // ADR-136 phase 4: optional fixed-term capture, symmetric with hire. Absent ->
  // the emitted batch is byte-for-byte today's [REHIRE, HIRE_FINALIZED]. Present ->
  // the batch additionally carries the production resign legs; the REHIRE handler
  // has already SKIPped any pending end of the OLD contract and cleared the
  // singletons before these legs run, so the new end lands cleanly on the
  // successor episode. No backend changes — the syncs are untouched (§2a.7).
  contractEndDate: z.iso.date('Contract end date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndReason: z.string().min(1).optional()
}).refine(
  (data) => !data.contractEndDate || data.contractEndDate > data.hireDate,
  { message: 'Contract end date must be after the contract start date', path: ['contractEndDate'] }
).refine(
  (data) => !data.contractEndDate || Boolean(data.contractEndReason && data.contractEndReason.trim()),
  { message: 'A fixed-term comment is required when a contract end date is set', path: ['contractEndReason'] }
);

/**
 * Rehire Former Employee
 *
 * Reactivates employment for a former employee returning to the company.
 * Note: Rehire must be followed by hire.finalized to set the new start date.
 *
 * @param {Object} payload - Rehire data
 * @param {string} payload.employeeId - Required - existing employee ID
 * @param {string} [payload.position] - Required 
 * @param {string|null} [payload.grade] - Optional - new grade; an explicit null means
 *   the new position has no grade (ADR-139), an omitted key means "leave unchanged"
 * @param {string} [payload.contractType] - Required
 * @param {string} [payload.contractCompany] - Required
 * @param {string} [payload.workingLocation] - Required
 * @param {string} [payload.cluster] - Required
 * @param {string} payload.hireDate - Required - ISO date format (YYYY-MM-DD)
 * @param {string} [payload.contractNumber] - Optional (ADR-136) - the number of the
 *   contract this rehire starts. Stamped onto the NEW episode as ADR-049
 *   `sourceReference` { source: 'radar', contractId }.
 * @param {string} [actorType='User'] - Actor type (System, User, Import)
 * @returns {Promise<Object>}
 * @throws {z.ZodError} - If validation fails
 */
export const rehire = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  // Validate payload
  const validatedPayload = rehireSchema.parse(payload);

  // ADR-136: the contract number must ride the REHIRE event, not hire.finalized.
  // In a rehire, handleHireFinalized detects the rehire scenario and REUSES the
  // episode the REHIRE handler just created — it never writes an episode payload —
  // so a sourceReference sent on hire.finalized would be silently dropped. The
  // REHIRE handler is the one that persists `payload` onto the new episode.
  const contractId = toSourceContractId(validatedPayload.contractNumber);
  // ADR-160 §5.4 — the stamp is unconditional: a radar contract without a
  // number used to carry no sourceReference at all, which radar-api reads as
  // radar-owned but the Americas sync reads as ADOPTABLE by start date. The
  // contract number joins the stamp when there is one.
  const sourceReference = contractId === null
    ? { source: DEFAULT_SOURCE_SYSTEM }
    : { source: DEFAULT_SOURCE_SYSTEM, contractId };

  // ADR-136 phase 4 (A2): one sentence, two destinations — the successor episode's
  // comment (via REHIRE.comments — hire.finalized's rehire branch reuses that
  // episode, so comments must ride REHIRE, exactly like the source reference) and
  // the resignation reason on the scheduled end.
  const fixedTermReason = validatedPayload.contractEndDate
    ? validatedPayload.contractEndReason.trim()
    : null;

  const events = [{
    type: EMPLOYMENT_EVENT_TYPES.REHIRE,
    payload: {
      employeeId: validatedPayload.employeeId,
      ...withEpisode(validatedPayload),
      positionId: validatedPayload.position,
      // ADR-139 — `!== undefined`, so an explicit null survives to the handler.
      ...(validatedPayload.grade !== undefined ? { gradeId: validatedPayload.grade } : {}),
      contractTypeId: validatedPayload.contractType,
      contractCompanyId: validatedPayload.contractCompany,
      workingLocationId: validatedPayload.workingLocation,
      clusterId: validatedPayload.cluster,
      contractStartDate: validatedPayload.hireDate,
      // ADR-138: rides REHIRE, like the source reference and for the same reason —
      // this is the handler that owns the employee update for a rehire.
      // hire.finalized's rehire branch reuses the episode REHIRE created and never
      // reaches its own updateEmployee call.
      ...(validatedPayload.employeeHireDate
        ? { hireDate: validatedPayload.employeeHireDate }
        : {}),
      ...(sourceReference ? { sourceReference } : {}),
      ...(fixedTermReason ? { comments: fixedTermReason } : {})
    }
  }, {
    type: EMPLOYMENT_EVENT_TYPES.HIRE_FINALIZED,
    payload: {
      employeeId: validatedPayload.employeeId,
      // ADR-155 §5 — the SUCCESSOR, from the REHIRE leg's return
      employmentEpisodeId: SUCCESSOR_EPISODE_PLACEHOLDER,
      hireDate: validatedPayload.hireDate
    }
  }];

  if (validatedPayload.contractEndDate) {
    // ADR-136 phase 4: the new contract's scheduled end, via the production resign
    // legs. Ordering matters and is safe: REHIRE (first in this same transaction)
    // marks the OLD contract's pending TERMINATION_FINALIZED as SKIPPED and clears
    // resignDate/resignReason, so these legs write the NEW end against the
    // successor episode with nothing stale in the way.
    events.push({
      type: EMPLOYMENT_EVENT_TYPES.RESIGNATION_SUBMITTED,
      payload: {
        employeeId: validatedPayload.employeeId,
        employmentEpisodeId: SUCCESSOR_EPISODE_PLACEHOLDER,
        resignationDate: validatedPayload.contractEndDate,
        resignationReason: fixedTermReason
      }
    }, {
      type: EMPLOYMENT_EVENT_TYPES.TERMINATION_FINALIZED,
      payload: {
        employeeId: validatedPayload.employeeId,
        employmentEpisodeId: SUCCESSOR_EPISODE_PLACEHOLDER
      }
    });
  }

  return await employmentApi.triggerEmploymentEvents({
    metadata: {
      actorType
    },
    events
  });
};
